import type { Location, Update } from 'history';
import { Action } from 'history';
import { useCallback, useEffect, useRef } from 'react';

import { getPageOffsetTopPx } from '@app/offset-anchor';

import { useNavigator } from './router.hooks';

export interface ScrollPosition {
  x: number;
  y: number;
}

type ScrollPositions = Map<string, ScrollPosition>;

const topOfPage: ScrollPosition = { x: 0, y: 0 };

// ~1s at 60fps. Counted in frames so a throttled tab still gets its attempts.
export const maxSettleFrames = 60;

// Once any of these fire the user is driving; stop moving the page under them.
const userInputEvents = [
  'keydown',
  'pointerdown',
  'touchstart',
  'wheel',
] as const;

const getCurrentScroll = (): ScrollPosition => ({
  x: window.scrollX,
  y: window.scrollY,
});

/**
 * Resolves where a location belongs: its saved offset, else its `#hash` target
 * clear of the page offset, else the top.
 */
const resolveTarget = (
  location: Location,
  positions: ScrollPositions,
): ScrollPosition => {
  const saved = positions.get(location.key);
  if (saved) return saved;

  const target =
    location.hash && document.getElementById(location.hash.slice(1));
  if (!target) return topOfPage;

  return {
    x: window.scrollX,
    y:
      window.scrollY +
      target.getBoundingClientRect().top -
      getPageOffsetTopPx(),
  };
};

export interface ScrollRestoration {
  /**
   * Called with every history update, replaces included.
   */
  onNavigate: (update: Update) => void;
  /**
   * Called once the destination's content is on screen.
   */
  onRouteCommit: () => void;
}

/**
 * Manual scroll handling for client navigations, standing in for the native
 * restoration the client switches off.
 */
export const useScrollRestoration = (): ScrollRestoration => {
  const history = useNavigator();

  const positionsRef = useRef<ScrollPositions>(new Map());

  // Tracked so an offset is stored against the page being left, not entered.
  const currentLocationRef = useRef(
    process.env.BROWSER ? history.location : ({} as Location),
  );

  const frameRef = useRef<number>();
  const teardownRef = useRef<() => void>();

  // Entry a settle loop is driving, so its offset isn't taken for the user's.
  const restoringKeyRef = useRef<string>();

  const cancel = useCallback(() => {
    if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    frameRef.current = undefined;

    restoringKeyRef.current = undefined;

    teardownRef.current?.();
    teardownRef.current = undefined;
  }, []);

  /**
   * Re-asserts a location's target every frame for the whole budget. Reaching
   * it is no reason to stop: a later wave of content can clamp the offset away.
   */
  const settle = useCallback(
    (location: Location) => {
      cancel();

      restoringKeyRef.current = location.key;

      const abort = () => cancel();
      userInputEvents.forEach(event =>
        window.addEventListener(event, abort, { passive: true }),
      );
      teardownRef.current = () =>
        userInputEvents.forEach(event =>
          window.removeEventListener(event, abort),
        );

      let framesLeft = maxSettleFrames;

      const step = () => {
        const target = resolveTarget(location, positionsRef.current);

        if (window.scrollX !== target.x || window.scrollY !== target.y) {
          window.scrollTo(target.x, target.y);
        }

        framesLeft -= 1;

        if (framesLeft <= 0) {
          cancel();
          return;
        }

        frameRef.current = requestAnimationFrame(step);
      };

      // Assert now rather than a frame later: called from the commit that puts
      // the destination on screen, this lands before its first paint.
      step();
    },
    [cancel],
  );

  // Held until the destination is on screen; scrolling earlier moves the
  // outgoing view, which stays mounted while the route resolves.
  const pendingRef = useRef<Location>();

  const onNavigate = useCallback(
    ({ action, location }: Update) => {
      const departing = currentLocationRef.current;

      // While a restore is running or queued for the departing entry the
      // current offset is the loop's, often clamped short, and not the user's.
      const isRestoringDeparting = restoringKeyRef.current === departing.key;
      const isPendingDeparting = pendingRef.current?.key === departing.key;
      const isDrivingDeparting = isRestoringDeparting || isPendingDeparting;

      if (!isDrivingDeparting) {
        positionsRef.current.set(departing.key, getCurrentScroll());
      }

      currentLocationRef.current = location;

      // Redirects replace too, so the action alone can't tell an in-place URL
      // rewrite from a real destination — the pathname and hash can.
      const isSamePage =
        action === Action.Replace &&
        location.pathname === departing.pathname &&
        location.hash === departing.hash;

      if (isSamePage) {
        // An in-place rewrite must not move the page. The old key is gone from
        // the stack, so move the offset rather than leaving a dead entry.
        const saved = positionsRef.current.get(departing.key);
        if (saved) {
          positionsRef.current.set(location.key, saved);
          positionsRef.current.delete(departing.key);
        }

        // A restore is still valid; re-aim it rather than settle a dead key.
        if (isRestoringDeparting) settle(location);
        if (isPendingDeparting) pendingRef.current = location;

        return;
      }

      cancel();

      if (action !== Action.Pop) {
        // A truncated forward stack can recycle a key, and a redirect discards
        // the entry it lands on; either way a held offset is stale.
        positionsRef.current.delete(location.key);
      }

      pendingRef.current = location;
    },
    [cancel, settle],
  );

  const onRouteCommit = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;

    pendingRef.current = undefined;

    // Even the top needs re-asserting: a virtualized table mounting into a
    // scrolled window adopts that offset and re-applies it as it measures.
    settle(pending);
  }, [settle]);

  useEffect(() => {
    if (!process.env.BROWSER) return undefined;

    // Native restoration is off, so an entry URL's fragment is ours too.
    if (history.location.hash) settle(history.location);

    return cancel;
  }, [cancel, history, settle]);

  return { onNavigate, onRouteCommit };
};
